// sparkler-milestone — b06 Olov. 30th milestone preview card.
//
// Pitch-black night sky. Maximalist constellation of fireworks bursting in
// gold/copper/red, sparkler sticks at every corner, a giant Bodoni "30"
// in saffron foil at the centre, names below in italic, oval portrait
// at the top, "BAYRAM" tracked-out caps and date/venue at the bottom.
// Falling-star streaks slash diagonally across the field.
import { Photo, type LayoutCtx } from "./_shared";

// ─── Static maximalist content ─────────────────────────────────────────

interface Burst { cx: number; cy: number; r: number; color: string; rays: number; }
const BURSTS: Burst[] = [
  { cx: 18, cy: 22, r: 26, color: "#E2A630", rays: 14 },
  { cx: 82, cy: 18, r: 22, color: "#C97B2C", rays: 12 },
  { cx: 88, cy: 70, r: 20, color: "#E2A630", rays: 12 },
  { cx: 12, cy: 78, r: 24, color: "#A6391F", rays: 14 },
  { cx: 50, cy: 8, r: 18, color: "#E2A630", rays: 12 },
];

interface Sparkler { x: number; y: number; angle: number; len: number; }
const SPARKLERS: Sparkler[] = [
  { x: 4, y: 12, angle: -25, len: 18 },
  { x: 96, y: 10, angle: 25, len: 18 },
  { x: 2, y: 50, angle: 0, len: 16 },
  { x: 98, y: 52, angle: 180, len: 16 },
  { x: 6, y: 92, angle: -150, len: 18 },
  { x: 94, y: 90, angle: 150, len: 18 },
  { x: 50, y: 96, angle: 90, len: 14 },
];

// 24 small twinkling dots scattered on the night sky.
const TWINKLES: Array<[number, number, number]> = [
  [10, 30, 1.2], [16, 60, 1.5], [24, 12, 1.0], [28, 44, 1.3],
  [34, 26, 1.1], [40, 64, 1.4], [44, 18, 1.2], [48, 50, 1.3],
  [56, 30, 1.0], [60, 68, 1.5], [66, 22, 1.2], [72, 50, 1.3],
  [78, 36, 1.1], [84, 60, 1.4], [90, 24, 1.2], [94, 46, 1.3],
  [20, 82, 1.2], [38, 88, 1.4], [54, 84, 1.1], [70, 86, 1.3],
  [22, 6, 1.2], [62, 4, 1.5], [78, 6, 1.1], [86, 84, 1.3],
];

// Falling-star streaks (x1, y1, x2, y2 in % space)
const STREAKS: Array<[number, number, number, number]> = [
  [0, 30, 22, 12],
  [78, 8, 100, 28],
  [10, 70, 32, 56],
  [70, 62, 96, 78],
];

function FireworkBurst({ b, accent }: { b: Burst; accent: string }) {
  const rays = Array.from({ length: b.rays });
  return (
    <g transform={`translate(${b.cx} ${b.cy})`}>
      {rays.map((_, i) => {
        const a = (i / b.rays) * Math.PI * 2;
        const x2 = Math.cos(a) * b.r;
        const y2 = Math.sin(a) * b.r;
        const xd = Math.cos(a) * (b.r * 0.62);
        const yd = Math.sin(a) * (b.r * 0.62);
        return (
          <g key={i}>
            <line
              x1={0}
              y1={0}
              x2={x2}
              y2={y2}
              stroke={b.color}
              strokeWidth={0.4}
              strokeLinecap="round"
              opacity={0.85}
            />
            <circle cx={x2} cy={y2} r={0.7} fill={b.color} />
            <circle cx={xd} cy={yd} r={0.35} fill={accent} opacity={0.7} />
          </g>
        );
      })}
      <circle r={1.4} fill={accent} opacity={0.95} />
    </g>
  );
}

function SparklerStick({ s, accent, ink }: { s: Sparkler; accent: string; ink: string }) {
  const sparks = Array.from({ length: 9 });
  return (
    <g transform={`translate(${s.x} ${s.y}) rotate(${s.angle})`}>
      {/* stick */}
      <line x1={0} y1={0} x2={s.len} y2={0} stroke={ink} strokeWidth={0.35} opacity={0.55} />
      {/* glowing tip */}
      <circle cx={s.len} cy={0} r={1.3} fill={accent} opacity={0.95} />
      <circle cx={s.len} cy={0} r={2.6} fill={accent} opacity={0.18} />
      {/* radiating sparks */}
      {sparks.map((_, i) => {
        const a = (i / sparks.length) * Math.PI * 2;
        const r = 4 + (i % 3);
        return (
          <circle
            key={i}
            cx={s.len + Math.cos(a) * r}
            cy={Math.sin(a) * r}
            r={0.45}
            fill={accent}
            opacity={0.8 - (i % 3) * 0.18}
          />
        );
      })}
    </g>
  );
}

// ─── Layout ────────────────────────────────────────────────────────────

export function SparklerMilestoneLayout({ names, date, venue, accent, ink, font, photo }: LayoutCtx) {
  // Force the dark-mode palette regardless of host bg — this card's identity
  // is a black night sky.
  const sky = "#0E0B14";
  const cream = ink || "#F5EFE6";
  const gold = accent || "#E2A630";
  // Strip "30 yosh" / age suffix — the giant numeral carries it.
  const cleanName = names.replace(/\s*\d+\s*(yosh|yil)?\s*$/i, "").trim() || names;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `radial-gradient(ellipse at 50% 38%, #1B1326 0%, ${sky} 70%)`,
        color: cream,
        overflow: "hidden",
      }}
    >
      {/* ─── SVG night-sky layer (twinkles, bursts, sparklers, streaks) ─── */}
      <svg
        viewBox="0 0 100 140"
        preserveAspectRatio="xMidYMid slice"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      >
        {/* twinkles */}
        {TWINKLES.map(([x, y, r], i) => (
          <g key={`t${i}`}>
            <circle cx={x} cy={y * 1.4} r={r * 0.35} fill={cream} opacity={0.85} />
            <circle cx={x} cy={y * 1.4} r={r * 0.9} fill={cream} opacity={0.18} />
          </g>
        ))}

        {/* falling-star streaks */}
        {STREAKS.map(([x1, y1, x2, y2], i) => {
          const id = `streak-${i}`;
          return (
            <g key={`s${i}`}>
              <defs>
                <linearGradient id={id} x1={x1} y1={y1} x2={x2} y2={y2} gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor={gold} stopOpacity="0" />
                  <stop offset="80%" stopColor={gold} stopOpacity="0.85" />
                  <stop offset="100%" stopColor={cream} stopOpacity="1" />
                </linearGradient>
              </defs>
              <line
                x1={x1}
                y1={y1 * 1.4}
                x2={x2}
                y2={y2 * 1.4}
                stroke={`url(#${id})`}
                strokeWidth={0.35}
                strokeLinecap="round"
              />
              <circle cx={x2} cy={y2 * 1.4} r={0.6} fill={cream} />
            </g>
          );
        })}

        {/* fireworks bursts */}
        {BURSTS.map((b, i) => (
          <FireworkBurst key={`b${i}`} b={{ ...b, cy: b.cy * 1.4 }} accent={cream} />
        ))}

        {/* sparkler sticks at corners/edges */}
        {SPARKLERS.map((s, i) => (
          <SparklerStick key={`sp${i}`} s={{ ...s, y: s.y * 1.4 }} accent={gold} ink={cream} />
        ))}
      </svg>

      {/* ─── Foreground composition ─── */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8% 8% 7%",
        }}
      >
        {/* TOP — eyebrow + oval portrait */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 6,
              fontWeight: 700,
              letterSpacing: "0.42em",
              color: gold,
            }}
          >
            OTUZ YIL
          </div>
          <div
            style={{
              width: 56,
              height: 70,
              borderRadius: "50% / 44%",
              padding: 1.5,
              background: `linear-gradient(155deg, ${gold}, #C97B2C)`,
              boxShadow: `0 4px 14px ${gold}55`,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50% / 44%",
                overflow: "hidden",
                background: sky,
                position: "relative",
              }}
            >
              <Photo
                src={photo}
                style={{ position: "absolute", inset: 0 }}
                fallback={
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      background: `radial-gradient(circle at 50% 35%, ${gold}55, transparent 70%)`,
                    }}
                  />
                }
              />
            </div>
          </div>
        </div>

        {/* MIDDLE — Giant "30" + names */}
        <div style={{ textAlign: "center", lineHeight: 1, position: "relative" }}>
          {/* Sparkle trails near the 30 */}
          <span
            aria-hidden
            style={{
              position: "absolute",
              left: -14,
              top: -6,
              color: gold,
              fontSize: 8,
              opacity: 0.85,
            }}
          >
            ✦
          </span>
          <span
            aria-hidden
            style={{
              position: "absolute",
              right: -12,
              bottom: 14,
              color: cream,
              fontSize: 6,
              opacity: 0.85,
            }}
          >
            ✦
          </span>
          <div
            style={{
              fontFamily: font,
              fontStyle: "italic",
              fontSize: 96,
              fontWeight: 400,
              background: `linear-gradient(180deg, #F4D27A 0%, ${gold} 50%, #8E5A1A 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              color: gold,
              textShadow: `0 0 18px ${gold}66`,
              letterSpacing: "-0.04em",
            }}
          >
            30
          </div>
          <div
            style={{
              fontFamily: font,
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: 18,
              color: gold,
              marginTop: 2,
              letterSpacing: "-0.01em",
            }}
          >
            {cleanName}
          </div>
        </div>

        {/* BOTTOM — BAYRAM eyebrow, date, venue */}
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 8,
              fontWeight: 700,
              letterSpacing: "0.55em",
              color: gold,
              paddingLeft: "0.55em",
            }}
          >
            BAYRAM
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontFamily: "var(--font-ui)",
              fontSize: 6.2,
              fontWeight: 700,
              letterSpacing: "0.32em",
              color: cream,
            }}
          >
            <span style={{ width: 14, height: 1, background: gold, opacity: 0.7 }} />
            <span>{date}</span>
            <span>·</span>
            <span>{venue.toUpperCase()}</span>
            <span style={{ width: 14, height: 1, background: gold, opacity: 0.7 }} />
          </div>
        </div>
      </div>
    </div>
  );
}
